<script setup lang="ts">
import { RouterView, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/data'
import { useImpersonationStore } from '@/stores/impersonation'
import { useAlertsStore } from '@/stores/alerts'
import { setGlobalAlertHandler } from '@/lib/api'
import type { AlertType } from '@/stores/alerts'
import { ref, onMounted } from 'vue'
import ImpersonationBanner from '@/components/display/ImpersonationBanner.vue'
import ScrollToTopButton from '@/components/display/ScrollToTopButton.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import LayoutShell from '@/components/layout/LayoutShell.vue'

const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const impersonationStore = useImpersonationStore()
const alertsStore = useAlertsStore()
const router = useRouter()
const appReady = ref(false)

// Google Analytics - only initializes when VITE_GA_MEASUREMENT_ID is set.
// Leave the env var empty on dev/stage so only production traffic is tracked.
const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID
if (gaMeasurementId) {
  // Set up dataLayer before the script loads so early events are queued
  ;(window as any).dataLayer = (window as any).dataLayer || []
  ;(window as any).gtag = function gtag() { ;(window as any).dataLayer.push(arguments) }
  ;(window as any).gtag('js', new Date())
  ;(window as any).gtag('config', gaMeasurementId, { send_page_view: false })

  const gaScript = document.createElement('script')
  gaScript.async = true
  gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`
  document.head.appendChild(gaScript)

  // Track SPA page views on every route change
  router.afterEach((to) => {
    ;(window as any).gtag('event', 'page_view', { page_path: to.fullPath })
  })
}

// Register global API alert handler. Fires for 429, 401, and 5xx responses.
setGlobalAlertHandler((type, message, status) => {
  alertsStore.push({ type: type as AlertType, message })
  if (status === 401) {
    authStore.logout().catch(() => {}).finally(() => router.push('/'))
  }
})

onMounted(async () => {
  // Impersonation session is restored from sessionStorage synchronously in the store.
  // Skip the normal Firebase auth + tenant load for impersonation sessions.
  if (!impersonationStore.isImpersonating) {
    await authStore.initAuth()
    if (authStore.isAuthenticated) {
      await workspaceStore.loadWorkspaces()
    }
  }
  appReady.value = true
})
</script>

<template>
  <div class="app">
    <ImpersonationBanner />
    <LayoutShell v-if="appReady">
      <RouterView />
    </LayoutShell>
    <ScrollToTopButton />
    <ConfirmModal />
  </div>
</template>

<style scoped>
.app {
  min-height: 100vh;
}
</style>
